import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AppErrorBoundary, NotFound } from './components/AppErrorBoundary';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import { MobileBookBar } from './components/layout/MobileBookBar';
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import Contact from './pages/Contact';
import PrivateSessions from './pages/PrivateSessions';
import PrivateConsultation from './pages/PrivateConsultation';
import HeadshotsPortraits from './pages/HeadshotsPortraits';
import PetPhotography from './pages/PetPhotography';
import RealEstatePhotography from './pages/RealEstatePhotography';
import AerialPhotosVideo from './pages/AerialPhotosVideo';
import Book from './pages/Book';
import VirtualTwilight from './pages/VirtualTwilight';
import PhotoRestoration from './pages/PhotoRestoration';
import DigitalPersonaDevelopment from './pages/DigitalPersonaDevelopment';
import DigitalPersonaOrder from './pages/DigitalPersonaOrder';
import WebsiteDesign from './pages/WebsiteDesign';
import AISystemsAutomation from './pages/AISystemsAutomation';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import EmailConfirmed from './pages/EmailConfirmed';
import Welcome from './pages/Welcome';
import Account from './pages/Account';
import Orders from './pages/Orders';
import NewOrder from './pages/NewOrder';
import OrderDetail from './pages/OrderDetail';
import PhotoRestorationOrder from './pages/order/PhotoRestorationOrder';
import LoraTrainingOrder from './pages/order/LoraTrainingOrder';
import PhotoScanningOrder from './pages/order/PhotoScanningOrder';
import OrderSuccess from './pages/order/OrderSuccess';
import { LegacyBookRedirect } from './components/booking/LegacyBookRedirect';
import VacantLandSuccess from './pages/VacantLandSuccess';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminCreateClientOrder from './pages/admin/AdminCreateClientOrder';
import AdminUserDetail from './pages/admin/AdminUserDetail';
import AdminOrderDetail from './pages/admin/AdminOrderDetail';
import AdminOrdersList from './pages/admin/AdminOrdersList';
import AdminSettings from './pages/admin/AdminSettings';
import AdminPortfolio from './pages/admin/AdminPortfolio';
import AdminOrders from './pages/AdminOrders';
import AdminOrderDetailNew from './pages/AdminOrderDetail';
import PropertyGallery from './pages/PropertyGallery';
import Portfolio from './pages/Portfolio';
import PortfolioShowcase from './pages/PortfolioShowcase';
import PublicShowcase from './pages/PublicShowcase';
import ListingPage from './pages/ListingPage';
import ListingEditor from './pages/ListingEditor';
import { SiteChrome } from './components/layout/SiteChrome';
import InvoicePage from './pages/InvoicePage';
import MockPaymentPage from './pages/MockPaymentPage';
import OrderHub from './pages/OrderHub';
import ImpersonationBanner from './components/ImpersonationBanner';
import DevToolsPanel from './components/DevToolsPanel';
import { Toaster } from "@/components/ui/sonner";
import { initPostHog, PostHogPageView } from './lib/posthog';

// Pricing pages
import PricingCategories from './pages/pricing/PricingCategories';
import PricingRealEstate from './pages/pricing/PricingRealEstate';
import PricingPortraits from './pages/pricing/PricingPortraits';
import PricingVideoBrand from './pages/pricing/PricingVideoBrand';
import PricingCreativeDigital from './pages/pricing/PricingCreativeDigital';

// Initialize PostHog analytics
initPostHog();

const RouteErrorBoundary = ({ children }) => {
  const location = useLocation();
  return <AppErrorBoundary resetKey={location.pathname}>{children}</AppErrorBoundary>;
};

const LegacyUserRedirect = ({ suffix = '' }) => {
  const { userId } = useParams();
  return <Navigate to={`/admin/clients/${userId}${suffix}`} replace />;
};

function App() {
  return (
    <HelmetProvider>
      <Router>
        <AuthProvider>
          <PostHogPageView />
          <ScrollToTop />
          <ImpersonationBanner />
          <div className="flex flex-col min-h-screen bg-background font-sans">
            <SiteChrome><Navbar /></SiteChrome>
            <main className="flex-grow">
              <RouteErrorBoundary>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/services" element={<Services />} />
                <Route path="/services/private-sessions" element={<PrivateSessions />} />
                <Route path="/services/headshots-portraits" element={<HeadshotsPortraits />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              
              {/* Auth Pages */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/account" element={
                <ProtectedRoute>
                  <Account />
                </ProtectedRoute>
              } />
              
              {/* Orders Pages (Protected) */}
              <Route path="/orders" element={
                <ProtectedRoute>
                  <Orders />
                </ProtectedRoute>
              } />
              <Route path="/orders/new" element={
                <ProtectedRoute>
                  <NewOrder />
                </ProtectedRoute>
              } />
              <Route path="/orders/:orderId" element={
                <ProtectedRoute>
                  <OrderDetail />
                </ProtectedRoute>
              } />
              
              {/* Service-Specific Order Pages (Protected) */}
              <Route path="/order" element={
                <ProtectedRoute>
                  <OrderHub />
                </ProtectedRoute>
              } />
              <Route path="/order/photo-restoration" element={
                <ProtectedRoute>
                  <PhotoRestorationOrder />
                </ProtectedRoute>
              } />
              <Route path="/order/lora-training" element={
                <ProtectedRoute>
                  <LoraTrainingOrder />
                </ProtectedRoute>
              } />
              <Route path="/order/photo-scanning" element={
                <ProtectedRoute>
                  <PhotoScanningOrder />
                </ProtectedRoute>
              } />
              
              {/* Order Success - Public (no auth required for post-payment redirect) */}
              <Route path="/order/success" element={<OrderSuccess />} />
              
              {/* Real Estate Order - Accessible without auth, prompts at submission */}
              <Route path="/book" element={<Book />} />
              <Route path="/order/real-estate" element={<LegacyBookRedirect type="home" />} />
              <Route path="/order/vacant-land" element={<LegacyBookRedirect type="land" />} />
              <Route path="/order/vacant-land/success" element={<ProtectedRoute><VacantLandSuccess /></ProtectedRoute>} />
              
              {/* Email Confirmation - Public (handles Supabase redirect) */}
              <Route path="/email-confirmed" element={<EmailConfirmed />} />
              
              {/* Welcome Page - For first-time users (protected) */}
              <Route path="/welcome" element={
                <ProtectedRoute>
                  <Welcome />
                </ProtectedRoute>
              } />
              
              {/* Admin Pages (Protected + Admin Role) */}
              <Route path="/listing-editor/:gallerySlug" element={
                <ProtectedRoute>
                  <ListingEditor />
                </ProtectedRoute>
              } />
              <Route path="/admin" element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/clients" element={
                <ProtectedRoute>
                  <AdminUsers />
                </ProtectedRoute>
              } />
              <Route path="/admin/clients/:userId" element={
                <ProtectedRoute>
                  <AdminUserDetail />
                </ProtectedRoute>
              } />
              <Route path="/admin/clients/:userId/new-order" element={
                <ProtectedRoute>
                  <AdminCreateClientOrder />
                </ProtectedRoute>
              } />
              {/* Old /admin/users links keep working */}
              <Route path="/admin/users" element={<Navigate to="/admin/clients" replace />} />
              <Route path="/admin/users/:userId" element={<LegacyUserRedirect />} />
              <Route path="/admin/users/:userId/new-order" element={<LegacyUserRedirect suffix="/new-order" />} />
              <Route path="/admin/orders" element={
                <ProtectedRoute>
                  <AdminOrdersList />
                </ProtectedRoute>
              } />
              <Route path="/admin/orders/:orderId" element={
                <ProtectedRoute>
                  <AdminOrderDetail />
                </ProtectedRoute>
              } />
              
              {/* New Admin Orders (Phase 2) */}
              <Route path="/admin/real-estate-orders" element={
                <ProtectedRoute>
                  <AdminOrders />
                </ProtectedRoute>
              } />
              <Route path="/admin/real-estate-orders/:orderId" element={
                <ProtectedRoute>
                  <AdminOrderDetailNew />
                </ProtectedRoute>
              } />
              
              {/* Admin Settings */}
              <Route path="/admin/settings" element={
                <ProtectedRoute>
                  <AdminSettings />
                </ProtectedRoute>
              } />
              
              {/* Admin Portfolio Management */}
              <Route path="/admin/portfolio" element={
                <ProtectedRoute>
                  <AdminPortfolio />
                </ProtectedRoute>
              } />
              
              {/* Property Gallery - Public access with auth/token check */}
              <Route path="/property/:slug" element={<PropertyGallery />} />
              
              {/* Invoice Page - Requires auth */}
              <Route path="/invoice/:invoiceId" element={
                <ProtectedRoute>
                  <InvoicePage />
                </ProtectedRoute>
              } />
              <Route path="/invoice/:invoiceId/success" element={
                <ProtectedRoute>
                  <InvoicePage />
                </ProtectedRoute>
              } />
              <Route path="/invoice/:invoiceId/mock-pay" element={
                <ProtectedRoute>
                  <MockPaymentPage />
                </ProtectedRoute>
              } />
            
            {/* Service Placeholder Pages - Redirect to relevant pricing/contact */}
            <Route path="/services/photography" element={<RealEstatePhotography />} />
            <Route path="/services/real-estate-photography" element={<RealEstatePhotography />} />
            <Route path="/services/video-tours" element={<Navigate to="/pricing/real-estate" replace />} />
            <Route path="/services/aerial-photos-video" element={<AerialPhotosVideo />} />
            <Route path="/services/aerial-photos-&-video" element={<AerialPhotosVideo />} />
            <Route path="/services/brochures-&-flyers" element={<Navigate to="/pricing/real-estate" replace />} />
            <Route path="/services/property-websites" element={<Navigate to="/services/website-design" replace />} />
            <Route path="/services/floor-plans" element={<Navigate to="/pricing/real-estate" replace />} />
            <Route path="/services/frequently-asked-questions" element={<Navigate to="/contact" replace />} />
            <Route path="/services/brand-videos" element={<Navigate to="/pricing/video-brand" replace />} />
            <Route path="/services/virtual-twilight" element={<VirtualTwilight />} />
            <Route path="/services/vertical-social-videos" element={<Navigate to="/pricing/video-brand" replace />} />
            
            {/* Portrait & Photography services */}
            <Route path="/services/couples-engagements" element={<Navigate to="/pricing/portraits" replace />} />
            <Route path="/services/pet-photography" element={<PetPhotography />} />
            <Route path="/services/events-lifestyle" element={<Navigate to="/pricing/portraits" replace />} />
            
            {/* Video & Brand Content */}
            <Route path="/services/short-form-content" element={<Navigate to="/pricing/video-brand" replace />} />
            <Route path="/services/marketing-reels" element={<Navigate to="/pricing/video-brand" replace />} />
            
            {/* Creative & Digital services */}
            <Route path="/services/custom-artwork-prints" element={<Navigate to="/pricing/creative-digital" replace />} />
            <Route path="/services/ai-upscaling-restoration" element={<PhotoRestoration />} />
            <Route path="/services/photo-restoration" element={<PhotoRestoration />} />
            <Route path="/services/photo-restoration-enhancement" element={<PhotoRestoration />} />
            <Route path="/services/digital-persona-development" element={<DigitalPersonaDevelopment />} />
            <Route path="/services/digital-persona-development/order" element={<DigitalPersonaOrder />} />
            <Route path="/services/creative-retouching" element={<Navigate to="/pricing/creative-digital" replace />} />
            <Route path="/services/website-design" element={<WebsiteDesign />} />
            <Route path="/services/ai-systems-automation" element={<AISystemsAutomation />} />
            <Route path="/services/it-digital-services" element={<Navigate to="/services/ai-systems-automation" replace />} />
            
            <Route path="/private-consultation" element={<PrivateConsultation />} />
            
            {/* Pricing Pages - Category Selector + Sub-pages */}
            <Route path="/pricing" element={<PricingCategories />} />
            <Route path="/pricing/real-estate" element={<PricingRealEstate />} />
            <Route path="/pricing/portraits" element={<PricingPortraits />} />
            <Route path="/pricing/video-brand" element={<PricingVideoBrand />} />
            <Route path="/pricing/creative-digital" element={<PricingCreativeDigital />} />
            
            {/* Portfolio - Real Estate Showcase */}
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/portfolio/:slug" element={<PortfolioShowcase />} />
            
            {/* Public Showcase - for social sharing (paid galleries only) */}
            <Route path="/showcase/:slug" element={<PublicShowcase />} />
            <Route path="/listing/:slug" element={<ListingPage />} />
            
            {/* Redirect placeholder pages to relevant destinations */}
            <Route path="/work-with-me" element={<Navigate to="/contact" replace />} />
            <Route path="/blog" element={<Navigate to="/about" replace />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/order-services" element={<LegacyBookRedirect type="other" />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </RouteErrorBoundary>
        </main>
        <SiteChrome><Footer /><MobileBookBar /></SiteChrome>
        <Toaster />
        {/* Dev Tools Panel - only in development/preview */}
        {process.env.NODE_ENV !== 'production' && <DevToolsPanel />}
      </div>
      </AuthProvider>
    </Router>
    </HelmetProvider>
  );
}

export default App;
